"use client";

import React from "react";
import Navbar from "../../components/account/Navbar";
import "../../styles/clinicsetup.css";
import { useRef, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClinicAdditionModal from "../../components/modals/Clinicadditionmodal";
import { resetClinicData, useClinicData } from "../../hooks/useClinicData";
import { showErrorToast } from "../../utils/notificationUtils";
import {
  parseClinicApiError,
  getFirstSectionWithErrors,
} from "../../utils/clinicErrorUtils";
import api, { clinicAPI } from "../../services/api";
import BasicInfoSection from "./setup-components/BasicInfoSection";
import BusinessDetailsSection from "./setup-components/BusinessDetailsSection";
import ServicesSection from "./setup-components/ServicesSection";
import LocationSection from "./setup-components/LocationSection";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
const STEP_KEYS = ["basicInfo", "businessDetails", "location", "categories"];

const AddClinic = ({ clinicData }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { refresh, getClinicId } = useClinicData();

  const [currentSection, setCurrentSection] = useState(0);
  const LAST_SECTION_INDEX = 3;
  const fileInputRef = useRef(null);
  const dateInputRef = useRef(null);
  const formScrollContainerRef = useRef(null);
  const stepNavigationRef = useRef(null);

  const stepLabels = useMemo(
    () => STEP_KEYS.map((key) => t(`clinicSetup.steps.${key}`)),
    [t, i18n.language]
  );

  // Scroll to top when section changes
  useEffect(() => {
    // Use setTimeout to ensure DOM has updated with new section content
    const scrollToTop = setTimeout(() => {
      // Try multiple scroll methods to ensure it works
      // 1. Scroll the form container directly
      if (formScrollContainerRef.current) {
        formScrollContainerRef.current.scrollTop = 0;
      }
      
      // 2. Scroll step navigation into view (this works on any scrollable ancestor)
      if (stepNavigationRef.current) {
        stepNavigationRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
      
      // 3. Scroll window/document as fallback
      document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
      document.body.scrollTo({ top: 0, behavior: 'smooth' });
    }, 200);
    
    return () => clearTimeout(scrollToTop);
  }, [currentSection]);
  const [errors, setErrors] = useState([]);
  const [error, setError] = useState("");
  const [showClinicAdditionModal, setShowClinicAdditionModal] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [clinicTypeOptions, setClinicTypeOptions] = useState([]);

  const token = localStorage.getItem("access_token");
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // All form data in one state object
  const [form, setForm] = useState({
    logo: null,
    name_en: "",
    name_ar: "",
    establishmentDate: "",
    type: "",
    services: "",
    registrationNumber: "",
    vatNumber: "",
    currency: "",
    invoiceStart: "1",
    servicesOffered: [],
    email: "",
    telephone: "",
    mobile: "",
    website: "",
    country: "",
    city: "",
    street: "",
    buildingNumber: "",
    floorNumber: "",
    map_coordinates: {},
    latitude: "",
    longitude: "",
    mapSearchQuery: "",
    workingHours: {
      sunday: { enabled: false, from: "", to: "" },
      monday: { enabled: false, from: "", to: "" },
      tuesday: { enabled: false, from: "", to: "" },
      wednesday: { enabled: false, from: "", to: "" },
      thursday: { enabled: false, from: "", to: "" },
      friday: { enabled: false, from: "", to: "" },
      saturday: { enabled: false, from: "", to: "" },
    },
    is_pet_store: false,
  });

  function isSectionValid(sectionIdx) {
    switch (sectionIdx) {
      case 0:
        return (
          form.name_en && form.name_ar && form.type && form.email && form.mobile
        );
      case 1:
        return (
          form.registrationNumber &&
          form.vatNumber &&
          form.currency &&
          form.invoiceStart &&
          Object.values(form.workingHours).some((day) => day.enabled)
        );
      case 2:
        return (
          form.country &&
          form.city &&
          form.street &&
          form.buildingNumber &&
          form.latitude &&
          form.longitude
        );
      case 3:
        return form.servicesOffered.length > 0;
      default:
        return true;
    }
  }

  const validateCurrentSection = () => {
    const newErrors = {};
    
    switch (currentSection) {
      case 0:
        if (!form.name_en) newErrors.name_en = t("clinicSetup.validation.nameEnRequired");
        if (!form.name_ar) newErrors.name_ar = t("clinicSetup.validation.nameArRequired");
        if (!form.type) newErrors.type = t("clinicSetup.validation.typeRequired");
        if (!form.email) newErrors.email = t("clinicSetup.validation.emailRequired");
        if (!form.mobile) newErrors.mobile = t("clinicSetup.validation.mobileRequired");
        break;
      case 1:
        if (!form.registrationNumber)
          newErrors.registrationNumber = t("clinicSetup.validation.registrationNumberRequired");
        if (!form.vatNumber) newErrors.vatNumber = t("clinicSetup.validation.vatNumberRequired");
        if (!form.currency) newErrors.currency = t("clinicSetup.validation.currencyRequired");
        if (!form.invoiceStart) newErrors.invoiceStart = t("clinicSetup.validation.invoiceStartRequired");
        if (!Object.values(form.workingHours).some((day) => day.enabled)) {
          newErrors.workingHours = t("clinicSetup.validation.workingHoursRequired");
        }
        break;
      case 2:
        if (!form.country) newErrors.country = t("clinicSetup.validation.countryRequired");
        if (!form.city) newErrors.city = t("clinicSetup.validation.cityRequired");
        if (!form.street) newErrors.street = t("clinicSetup.validation.streetRequired");
        if (!form.buildingNumber)
          newErrors.buildingNumber = t("clinicSetup.validation.buildingNumberRequired");
        break;
      case 3:
        if (form.servicesOffered.length === 0) {
          newErrors.servicesOffered = t("clinicSetup.validation.servicesRequired");
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      showErrorToast(t("clinicSetup.alerts.requiredFields"));
      return false;
    }
    
    return true;
  };

  const validateAllSections = () => {
    const newErrors = {};
    
    // Section 0: Basic Info
    if (!form.name_en) newErrors.name_en = t("clinicSetup.validation.nameEnRequired");
    if (!form.name_ar) newErrors.name_ar = t("clinicSetup.validation.nameArRequired");
    if (!form.type) newErrors.type = t("clinicSetup.validation.typeRequired");
    if (!form.email) newErrors.email = t("clinicSetup.validation.emailRequired");
    if (!form.mobile) newErrors.mobile = t("clinicSetup.validation.mobileRequired");
    
    // Section 1: Business Details
    if (!form.registrationNumber)
      newErrors.registrationNumber = t("clinicSetup.validation.registrationNumberRequired");
    if (!form.vatNumber) newErrors.vatNumber = t("clinicSetup.validation.vatNumberRequired");
    if (!form.currency) newErrors.currency = t("clinicSetup.validation.currencyRequired");
    if (!form.invoiceStart) newErrors.invoiceStart = t("clinicSetup.validation.invoiceStartRequired");
    if (!Object.values(form.workingHours).some((day) => day.enabled)) {
      newErrors.workingHours = t("clinicSetup.validation.workingHoursRequired");
    }
    
    // Section 2: Location
    if (!form.country) newErrors.country = t("clinicSetup.validation.countryRequired");
    if (!form.city) newErrors.city = t("clinicSetup.validation.cityRequired");
    if (!form.street) newErrors.street = t("clinicSetup.validation.streetRequired");
    if (!form.buildingNumber)
      newErrors.buildingNumber = t("clinicSetup.validation.buildingNumberRequired");
 
    // Section 3: Services
    if (form.servicesOffered.length === 0) {
      newErrors.servicesOffered = t("clinicSetup.validation.servicesRequired");
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      showErrorToast(t("clinicSetup.alerts.requiredFields"));
      return false;
    }
    
    return true;
  };

  useEffect(() => {
    // Use centralized data if available
    if (clinicData?.clinic_types) {
      setClinicTypeOptions(
        clinicData.clinic_types.map((type) => ({
          value: type.id,
          label: type.name_en,
        }))
      );
    } else {
      const fetchClinicTypes = async () => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/v1/clinic-types/active`
          );
          if (!response.ok) throw new Error("Failed to fetch clinic types");
          const result = await response.json();
          if (Array.isArray(result.data)) {
            setClinicTypeOptions(
              result.data.map((type) => ({
                value: type.id,
                label: type.name_en,
              }))
            );
          }
        } catch (err) {
          console.error("Error fetching clinic types:", err);
        }
      };
      fetchClinicTypes();
    }
  }, [clinicData]);

  // Countries/cities are NOT loaded from clinic setup data (the endpoint skips
  // them to keep the payload light). They are lazy-loaded from
  // /locations/countries the first time the user reaches the location section
  // or focuses the country dropdown, and cached for the session.
  const countriesFetchedRef = useRef(false);
  const loadCountries = async () => {
    if (countriesFetchedRef.current) return;
    countriesFetchedRef.current = true;
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/locations/countries`);
      if (!response.ok) {
        throw new Error("Failed to fetch countries");
      }
      const result = await response.json();
      if (result.status === "success" && Array.isArray(result.data)) {
        setCountries(result.data);
        const options = result.data.map((country) => ({
          value: country.id,
          label: country.name_en,
        }));
        setCountryOptions(options);
      }
    } catch (err) {
      console.error("Error fetching countries:", err);
      setError(t("clinicSetup.errors.loadCountries"));
      // Allow retrying on next focus/section visit
      countriesFetchedRef.current = false;
    }
  };

  // Load countries when the user navigates to the location section
  useEffect(() => {
    if (currentSection === 2) {
      loadCountries();
    }
  }, [currentSection]);

  useEffect(() => {
    if (!form.country) return;

    // Cities come from the lazy-loaded countries (which include their cities)
    const selectedCountry = countries.find(
      (country) => country.id.toString() === form.country.toString()
    );
    if (selectedCountry && selectedCountry.cities && selectedCountry.cities.length > 0) {
      const options = selectedCountry.cities.map((city) => ({
        value: city.id,
        label: city.name_en,
      }));
      setCityOptions(options);
      return;
    }

    // Fallback to API call if the country's cities aren't embedded
    const fetchCities = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/locations/cities/country/${form.country}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch cities");
        }
        const result = await response.json();
        if (result.status === "success" && Array.isArray(result.data)) {
          const options = result.data.map((city) => ({
            value: city.id,
            label: city.name_en,
          }));
          setCityOptions(options);
        }
      } catch (err) {
        console.error("Error fetching cities:", err);
        setError(t("clinicSetup.errors.loadCities"));
      }
    };
    fetchCities();
  }, [form.country, countries]);

  const [clinicServices, setClinicServices] = useState([]);

  useEffect(() => {
    if (clinicData?.clinic_service_list) {
      setClinicServices(clinicData.clinic_service_list);
    } else {
      async function fetchClinicServices() {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/v1/clinic-service-list`
          );
          if (!response.ok) throw new Error("Failed to fetch clinic services");
          const result = await response.json();
          const flatServices = Array.isArray(result.data)
            ? result.data.flat()
            : [];
          setClinicServices(flatServices);
        } catch (err) {
          console.error("Error fetching clinic services:", err);
        }
      }
      fetchClinicServices();
    }
  }, [clinicData]);

  // Refresh clinic data when success modal opens
  useEffect(() => {
    if (showClinicAdditionModal) {
      const refreshClinicData = async () => {
        try {
          let clinicId = null;
          const userData = localStorage.getItem("user_data");
          if (userData && userData !== "undefined" && userData !== "null") {
            const userDataParsed = JSON.parse(userData);
            clinicId = userDataParsed.clinic_id || null;
          }
          
          const params = clinicId ? { clinic_id: clinicId } : {};
          await clinicAPI.getClinicSetupData(params);
          // Use refresh function to trigger update across all components
          refresh();
          
          // After refresh, navigate to edit page if we have a clinic ID
          if (clinicId) {
            // Small delay to ensure data is refreshed
            setTimeout(() => {
              // Navigate to edit route with clinicId in URL
              navigate(`/medical/clinic/${clinicId}/edit`);
              // Also dispatch event to notify parent component if needed
              window.dispatchEvent(new CustomEvent('clinicCreated', { detail: { clinicId } }));
            }, 500);
          }
        } catch (error) {
          console.error('Error refreshing clinic data:', error);
          // Silently fail - this is a background refresh
        }
      };
      
      refreshClinicData();
    }
  }, [showClinicAdditionModal, refresh, navigate]);

  async function createClinic(clinicData) {
    const formdata = new FormData();
    formdata.append("name_en", clinicData.name_en);
    formdata.append("name_ar", clinicData.name_ar);
    formdata.append("registration_number", clinicData.registrationNumber);
    const establishmentDate = clinicData.establishmentDate || clinicData.establishment_date;
    if (establishmentDate) {
      formdata.append("establishment_date", establishmentDate);
    } else {
      formdata.append("establishment_date", "");
    }
    formdata.append("type", parseInt(clinicData.type, 10) || "");
    formdata.append("vat_number", clinicData.vatNumber);
    formdata.append("currency", clinicData.currency);
    formdata.append(
      "invoice_starting_number",
      clinicData.invoiceStart || clinicData.invoice_starting_number
    );
    formdata.append("telephone", clinicData.telephone);
    formdata.append("mobile", clinicData.mobile);
    formdata.append("email", clinicData.email);
    let website = clinicData.website?.trim() || "";
    if (website) {
      if (!/^https?:\/\//i.test(website)) {
        website = `https://${website}`;
      }
      formdata.append("website", website);
    }
    formdata.append("country", clinicData.country);
    formdata.append("city", clinicData.city);
    formdata.append("country_id", clinicData.country);
    formdata.append("city_id", clinicData.city);
    formdata.append("street", clinicData.street);
    formdata.append("building_number", clinicData.buildingNumber);
    formdata.append("floor_number", clinicData.floorNumber);
    if (clinicData.latitude && clinicData.longitude) {
      formdata.append("latitude", parseFloat(clinicData.latitude));
      formdata.append("longitude", parseFloat(clinicData.longitude));
    }
    if (clinicData.logo) {
      formdata.append("logo", clinicData.logo);
    }
    (clinicData.servicesOffered || []).forEach((id) => {
      formdata.append("services_offered[]", id);
    });

    // Convert lowercase day names to uppercase for API (e.g., "sunday" -> "SUN")
    const dayNameMap = {
      sunday: "sunday",
      monday: "monday",
      tuesday: "tuesday",
      wednesday: "wednesday",
      thursday: "thursday",
      friday: "friday",
      saturday: "saturday",
    };
    
    const workingHoursArray = Object.entries(clinicData.workingHours || {})
      .filter(([day, value]) => value.enabled)
      .map(([day, value]) => ({ 
        day: dayNameMap[day.toLowerCase()] || day.toUpperCase().slice(0, 3), 
        ...value 
      }));
    workingHoursArray.forEach((obj, idx) => {
      formdata.append(`working_hours[${idx}][day]`, obj.day);
      formdata.append(`working_hours[${idx}][enabled]`, obj.enabled ? 1 : 0);
      formdata.append(`working_hours[${idx}][from]`, obj.from);
      formdata.append(`working_hours[${idx}][to]`, obj.to);
    });

    formdata.append("is_pet_store", clinicData.is_pet_store ? "1" : "0");

    const myHeaders = new Headers();
    myHeaders.append("Accept", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);
    const response = await fetch(`${API_BASE_URL}/api/v1/clinics`, {
      method: "POST",
      headers: myHeaders,
      body: formdata,
    });

    const responseData = await response.json().catch(() => ({}));

    if (!response.ok) {
      const { message, fieldErrors } = parseClinicApiError(
        responseData,
        t("clinicSetup.errors.createClinic")
      );
      const detail = Object.values(fieldErrors || {}).join("\n");
      const finalMessage = detail || message;
      const error = new Error(finalMessage);
      error.fieldErrors = fieldErrors;
      error.status = response.status;
      throw error;
    }
    return responseData;
  }

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "logo" && files && files[0]) {
      setForm((prev) => ({ ...prev, logo: files[0] }));
      setLogoPreview(URL.createObjectURL(files[0]));
    } else {
      setForm((prev) => ({ ...prev, [name]: files ? files[0] : value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    
    // Validate all sections before submitting
    if (!validateAllSections()) {
      return;
    }
    try {
      setIsLoading(true);
      const response = await createClinic(form);
      if (response.status === 200 || response.status === "success") {
        const clinicId = response.data?.id;
        const isOwner = response.data?.is_owner || true;

        const userData = localStorage.getItem("user_data");
        if (userData && userData !== "undefined" && userData !== "null") {
          const userDataParsed = JSON.parse(userData);
          userDataParsed.clinic_id = clinicId;
          localStorage.setItem("user_data", JSON.stringify(userDataParsed));
        }

        if (clinicId) {
          localStorage.setItem(
            "clinic",
            JSON.stringify({ is_owner: isOwner, clinic_id: clinicId })
          );
          // Clinic ID will be in URL when navigating, no need to store in localStorage
        }

        setShowClinicAdditionModal(true);
      } else {
        throw new Error(response.message || t("clinicSetup.errors.createClinic"));
      }
    } catch (error) {
      const errorMessage = error?.message || t("clinicSetup.errors.createClinic");
      setSubmitError(errorMessage);
      if (error?.fieldErrors && Object.keys(error.fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...error.fieldErrors }));
        const firstSection = getFirstSectionWithErrors(error.fieldErrors);
        if (firstSection !== null) setCurrentSection(firstSection);
      }
      showErrorToast(errorMessage);
      setShowClinicAdditionModal(false);
      console.error("Error creating clinic:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleServiceChange = (serviceId) => (e) => {
    const { checked } = e.target;
    setForm((prev) => {
      let updated = [...(prev.servicesOffered || [])];
      if (checked) {
        if (!updated.includes(serviceId)) {
          updated.push(serviceId);
        }
      } else {
        updated = updated.filter((service) => service !== serviceId);
      }
      return { ...prev, servicesOffered: updated };
    });
  };

  return (
    <div
      className="clinicsetup-page-wrapper clinicinfo-member-wrapper"
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <Navbar />
      <div className="clinicinfo-member-wrapper" style={{
        background: "#fff",
        borderRadius: "0",
        margin: "0",
        minHeight: "calc(100vh - 80px)",
        boxShadow: "none",
        padding: "0",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 80px)",
        overflow: "hidden"
      }}>
        {/* Modern Step Navigation */}
        <div 
          ref={stepNavigationRef}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            padding: "24px 32px",
            paddingBottom: "24px",
            marginBottom: "0",
            borderBottom: "2px solid #e5e7eb",
            flexShrink: 0,
            width: "100%",
            maxWidth: "1040px",
            marginInline: "auto"
          }}
        >
          {stepLabels.map((label, idx) => (
              <React.Fragment key={STEP_KEYS[idx]}>
                <div
                  onClick={() => setCurrentSection(idx)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    userSelect: "none",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    transition: "all 0.2s ease",
                    background: currentSection === idx ? "rgba(0,212,179,0.12)" : "transparent"
                  }}
                  onMouseEnter={(e) => {
                    if (currentSection !== idx) {
                      e.currentTarget.style.background = "#f9fafb";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentSection !== idx) {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                >
                  <div style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    background: currentSection === idx ? "#00D4B3" : "#e5e7eb",
                    color: currentSection === idx ? "#0b2e2a" : "#6b7280",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: "600",
                    transition: "all 0.2s ease"
                  }}>
                    {idx + 1}
                  </div>
                  <span style={{
                    fontWeight: currentSection === idx ? "600" : "500",
                    color: currentSection === idx ? "#0f766e" : "#6b7280",
                    fontSize: "14px",
                    transition: "all 0.2s ease"
                  }}>
                    {label}
                  </span>
                </div>
                {idx < LAST_SECTION_INDEX && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{
                    color: "#d1d5db",
                    flexShrink: 0,
                    transform: i18n.dir() === "rtl" ? "scaleX(-1)" : "none"
                  }}>
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </React.Fragment>
            ))}
        </div>
        
        {/* Form Content - Scrollable area */}
        <div 
          ref={formScrollContainerRef}
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "0 32px 32px 32px",
            display: "flex",
            justifyContent: "center"
          }}
        >
          <form
            onSubmit={handleSubmit}
            style={{ height: "100%", width: "100%", maxWidth: "960px" }}
          >
          {currentSection === 0 && (
            <BasicInfoSection
              form={form}
              setForm={setForm}
              errors={errors}
              fileInputRef={fileInputRef}
              dateInputRef={dateInputRef}
              logoPreview={logoPreview}
              handleInputChange={handleInputChange}
              clinicTypeOptions={clinicTypeOptions}
              isLoading={isLoading}
            />
          )}
          {currentSection === 1 && (
            <BusinessDetailsSection
              form={form}
              errors={errors}
              setForm={setForm}
              handleInputChange={handleInputChange}
            />
          )}

          {currentSection === 2 && (
            <LocationSection
              form={form}
              errors={errors}
              countryOptions={countryOptions}
              cityOptions={cityOptions}
              setForm={setForm}
              handleInputChange={handleInputChange}
              onCountryFocus={loadCountries}
              setErrors={setErrors}
            />
          )}
          {currentSection === 3 && (
            <ServicesSection
              clinicServices={clinicServices}
              form={form}
              handleServiceChange={handleServiceChange}
              setForm={setForm}
              errors={errors}
            />
          )}

            {submitError && (
              <div
                style={{
                  color: "#e53935",
                  marginBottom: "16px",
                  textAlign: "center",
                }}
              >
                {submitError}
              </div>
            )}
            
            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                marginTop: "24px",
                paddingTop: "24px",
                borderTop: "2px solid #e5e7eb",
                flexShrink: 0
              }}
            >
              <button
                className="clinicsetup-btn clinicsetup-cancel-btn"
                style={{ marginRight: "10px" }}
                type="button"
                onClick={() => {
                  if (currentSection === 0) {
                    const clinicId = getClinicId();
                    if (clinicId) {
                      navigate(`/medical/clinic/${clinicId}/setup`);
                    } else {
                      navigate("/medical/clinic/setup");
                    }
                  } else {
                    setCurrentSection(currentSection - 1);
                  }
                }}
                disabled={currentSection === 0}
              >
                {t("clinicSetup.buttons.back")}
              </button>
              {currentSection < LAST_SECTION_INDEX ? (
                <button
                  className="clinicsetup-btn clinicsetup-next-btn"
                  type="button"
                  onClick={() => {
                    if (validateCurrentSection()) {
                      setCurrentSection(currentSection + 1);
                    }
                  }}
                >
                  {t("clinicSetup.buttons.next")}
                </button>
              ) : (
                <button
                  className="clinicsetup-next-btn"
                  type="button"
                  onClick={handleSubmit}
                >
                  {isLoading ? t("clinicSetup.buttons.saving") : t("clinicSetup.buttons.save")}
                </button>
              )}
            </div>
          </form>
        </div>
        <ClinicAdditionModal
          open={showClinicAdditionModal}
          onClose={() => {
            setShowClinicAdditionModal(false);
            resetClinicData();
          }}
          isUpdate={false}
        />
      </div>
    </div>
  );
};

export default AddClinic;

